import { ListChecks, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader } from '../_primitives';

export type HeadingTag = 'H1' | 'H2' | 'H3';

export interface StructureItem {
  id: string;
  tag: HeadingTag;
  title: string;
  /** When 'missing', the row renders as a dashed coral suggestion with an Add button. */
  flagged?: 'missing' | false;
}

export interface StructureChecklistCardProps {
  items: StructureItem[];
  onAddSuggested?: (item: StructureItem) => void;
}

const TAG_BG: Record<HeadingTag, string> = {
  H1: 'bg-coral text-white border-0',
  H2: 'bg-warm-bg text-muted-foreground border border-border',
  H3: 'bg-warm-bg text-muted-foreground border border-border',
};

const TAG_INDENT: Record<HeadingTag, string> = {
  H1: 'pl-1.5',
  H2: 'pl-3.5',
  H3: 'pl-6',
};

export function StructureChecklistCard({ items, onAddSuggested }: StructureChecklistCardProps) {
  const present = items.filter((i) => !i.flagged);

  // Per-level tally (H1:1, H2:5, H3:8…) like the WordPress plugin's structure
  // view, so structure can be sanity-checked without leaving the app.
  const counts: Record<HeadingTag, number> = { H1: 0, H2: 0, H3: 0 };
  for (const i of present) counts[i.tag] += 1;
  // A page should have exactly one H1 — surface 0-or-multiple (Camille's dup-H1 flag).
  const h1Anomaly = present.length > 0 && counts.H1 !== 1;

  return (
    <Card railSection="structure">
      <CardHeader
        icon={<ListChecks className="size-3.5" />}
        title="Structure"
        trailing={
          <span className="text-[10.5px] text-muted-foreground">{present.length} sections</span>
        }
      />
      {present.length > 0 && (
        <div className="px-3 -mt-1 pb-2 flex items-center gap-1.5">
          {(['H1', 'H2', 'H3'] as HeadingTag[]).map((t) => {
            const bad = t === 'H1' && h1Anomaly;
            return (
              <span
                key={t}
                title={
                  bad
                    ? `${counts.H1} H1 headings — a page should have exactly one`
                    : `${counts[t]} ${t} heading${counts[t] === 1 ? '' : 's'}`
                }
                className={cn(
                  'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold cursor-help',
                  bad
                    ? 'bg-destructive/10 text-destructive border border-destructive/40'
                    : counts[t] > 0
                      ? TAG_BG[t]
                      : 'bg-warm-bg text-muted-foreground/50 border border-border',
                )}
              >
                <span className="font-mono">{t}</span>
                <span className="tabular-nums">{counts[t]}</span>
              </span>
            );
          })}
        </div>
      )}
      <div className="px-3 pb-3 flex flex-col gap-0.5">
        {items.map((s) => {
          const isMissing = s.flagged === 'missing';
          return (
            <div
              key={s.id}
              className={cn(
                'flex items-center gap-1.5 py-1 pr-1.5 rounded-md',
                TAG_INDENT[s.tag],
                isMissing
                  ? 'bg-coral-light border border-dashed border-coral'
                  : 'border border-transparent',
              )}
            >
              <span
                className={cn(
                  'inline-block min-w-[22px] text-center px-1 rounded font-mono text-[9.5px] font-bold',
                  TAG_BG[s.tag],
                )}
              >
                {s.tag}
              </span>
              <span
                className={cn(
                  'flex-1 text-[11.5px] truncate',
                  s.tag === 'H1' ? 'font-semibold text-foreground' : 'font-medium text-foreground',
                  isMissing && 'italic opacity-85',
                )}
              >
                {s.title}
              </span>
              {isMissing && (
                <button
                  type="button"
                  onClick={() => onAddSuggested?.(s)}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-coral text-white border-0 hover:bg-coral-hover cursor-pointer"
                >
                  <Plus className="size-2.5" />
                  Add
                </button>
              )}
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="text-center text-[12px] text-muted-foreground py-3">
            No headings detected yet.
          </div>
        )}
      </div>
    </Card>
  );
}
